/**
 * useThemeStyled - Hook per applicare stili dinamici basati sul tema configuratore
 * 
 * Questo hook permette ai componenti di usare le variabili CSS del tema
 * in modo semplice e centralizzato.
 * 
 * Esempio d'uso:
 * const styles = useThemeStyled({
 *   card: true,
 *   text: 'title',
 *   padding: 'md'
 * });
 */

import { useThemeStore } from '../stores/themeStore';

export type ThemeSize = 'xs' | 'sm' | 'md' | 'lg' | 'xl' | '2xl';
export type ThemeTextVariant = 'title' | 'subtitle' | 'body' | 'caption' | 'muted';
export type ThemeSpacing = 'none' | 'xs' | 'sm' | 'md' | 'lg' | 'xl';

export interface ThemeStyledOptions {
  // Card options
  card?: boolean;
  cardHover?: boolean;
  
  // Text options
  text?: ThemeTextVariant;
  textSize?: ThemeSize;
  textWeight?: 'normal' | 'medium' | 'semibold' | 'bold';
  
  // Spacing options
  padding?: ThemeSpacing;
  margin?: ThemeSpacing;
  
  // Background options
  background?: 'primary' | 'secondary' | 'surface' | 'transparent';
  
  // Border options
  border?: boolean;
  borderRadius?: ThemeSize | 'full';
  
  // Glass options
  glass?: boolean;
  glassCard?: boolean;
  glassButton?: boolean;
  glassInput?: boolean;
  glassHeader?: boolean;
  glassSidebar?: boolean;
  glassModal?: boolean;
  glassBottomNav?: boolean;
}

export interface ThemeStyledReturn {
  // Classes ready to use
  className: string;
  
  // Inline styles for dynamic values
  style: React.CSSProperties;
  
  // Specific style getters
  getCardStyle: () => React.CSSProperties;
  getTextStyle: (variant?: ThemeTextVariant) => React.CSSProperties;
  getSpacingStyle: (type: 'padding' | 'margin', size?: ThemeSpacing) => React.CSSProperties;
  getGlassStyle: () => React.CSSProperties;
  
  // Color variables
  colors: {
    primary: string;
    secondary: string;
    text: string;
    textSecondary: string;
    textMuted: string;
    background: string;
    surface: string;
    border: string;
  };
  
  // Card variables
  card: {
    background: string;
    border: string;
    borderRadius: string;
    padding: string;
    shadow: string;
    shadowHover: string;
    titleColor: string;
    subtitleColor: string;
    textColor: string;
  };
  
  // Sidebar variables
  sidebar: {
    background: string;
    border: string;
    width: string;
    collapsedWidth: string;
    textColor: string;
    iconColor: string;
    hoverBg: string;
    activeBg: string;
    activeColor: string;
  };
  
  // Header variables
  header: {
    background: string;
    border: string;
    height: string;
    textColor: string;
    iconColor: string;
  };
  
  // Bottom Nav variables
  bottomNav: {
    background: string;
    border: string;
    height: string;
    iconColor: string;
    iconActiveColor: string;
    textColor: string;
  };
  
  // Layout variables
  layout: {
    maxWidth: string;
    padding: string;
    gridGap: string;
    containerBg: string;
    containerBorder: string;
  };
  
  // Typography variables
  typography: {
    fontSizeXs: string;
    fontSizeSm: string;
    fontSizeBase: string;
    fontSizeLg: string;
    fontSizeXl: string;
    fontSize2xl: string;
    fontWeightNormal: string;
    fontWeightMedium: string;
    fontWeightSemibold: string;
    fontWeightBold: string;
  };
  
  // Boat card variables
  boatCard: {
    imageBorderRadius: string;
    imageAspectRatio: string;
    titleFontSize: string;
    titleFontWeight: string;
    titleColor: string;
    categoryFontSize: string;
    categoryColor: string;
    yearColor: string;
    priceFontSize: string;
    priceFontWeight: string;
    priceColor: string;
  };
}

const SIZE_MAP: Record<ThemeSize, string> = {
  xs: '4px',
  sm: '8px',
  md: '16px',
  lg: '24px',
  xl: '32px',
  '2xl': '48px',
};

const SPACING_MAP: Record<ThemeSpacing, string> = {
  none: '0',
  xs: '4px',
  sm: '8px',
  md: '16px',
  lg: '24px',
  xl: '32px',
};

const TEXT_VARIANTS: Record<ThemeTextVariant, { color: string; fontSize: string; fontWeight: string }> = {
  title: {
    color: 'var(--glass-card-title-color, #0f172a)',
    fontSize: 'var(--text-size-xl, 1.25rem)',
    fontWeight: 'var(--text-weight-semibold, 600)',
  },
  subtitle: {
    color: 'var(--glass-card-subtitle-color, #475569)',
    fontSize: 'var(--text-size-base, 1rem)',
    fontWeight: 'var(--text-weight-medium, 500)',
  },
  body: {
    color: 'var(--color-text, #0f172a)',
    fontSize: 'var(--text-size-base, 1rem)',
    fontWeight: 'var(--text-weight-normal, 400)',
  },
  caption: {
    color: 'var(--color-text-secondary, #64748b)',
    fontSize: 'var(--text-size-sm, 0.875rem)',
    fontWeight: 'var(--text-weight-normal, 400)',
  },
  muted: {
    color: 'var(--color-text-muted, #94a3b8)',
    fontSize: 'var(--text-size-xs, 0.75rem)',
    fontWeight: 'var(--text-weight-normal, 400)',
  },
};

export function useThemeStyled(options: ThemeStyledOptions = {}): ThemeStyledReturn {
  const theme = useThemeStore((state) => state.getTheme());
  
  const {
    card: enableCard,
    cardHover,
    text,
    textSize,
    textWeight,
    padding,
    margin,
    background: bgOption,
    border: enableBorder,
    borderRadius,
    glass,
    glassCard,
    glassButton,
    glassInput,
    glassHeader,
    glassSidebar,
    glassModal,
    glassBottomNav,
  } = options;
  
  // Build className based on options
  const classNames: string[] = [];
  
  if (enableCard) classNames.push('theme-card');
  if (cardHover) classNames.push('theme-card-hover');
  if (enableBorder) classNames.push('theme-border');
  if (glass) classNames.push('glass-d-bg');
  if (glassCard) classNames.push('glass-d-card');
  if (glassButton) classNames.push('glass-d-button');
  if (glassInput) classNames.push('glass-d-input');
  if (glassHeader) classNames.push('glass-d-header');
  if (glassSidebar) classNames.push('glass-d-sidebar');
  if (glassModal) classNames.push('glass-d-modal');
  if (glassBottomNav) classNames.push('glass-d-bottom-nav');
  
  // Background class
  if (bgOption === 'primary') classNames.push('bg-[var(--color-primary)]');
  else if (bgOption === 'secondary') classNames.push('bg-[var(--color-secondary)]');
  else if (bgOption === 'surface') classNames.push('bg-[var(--color-surface)]');
  
  // Border radius
  if (borderRadius === 'full') classNames.push('rounded-full');
  else if (borderRadius) classNames.push(`rounded-${borderRadius}`);
  
  // Build inline styles
  const style: React.CSSProperties = {};
  
  // Padding
  if (padding) {
    const paddingValue = SPACING_MAP[padding];
    style.padding = paddingValue;
  }
  
  // Margin
  if (margin) {
    const marginValue = SPACING_MAP[margin];
    style.margin = marginValue;
  }
  
  // Text size
  if (textSize) {
    style.fontSize = `var(--text-size-${textSize})`;
  }
  
  // Text weight
  if (textWeight) {
    style.fontWeight = textWeight === 'normal' ? 400 : textWeight === 'medium' ? 500 : textWeight === 'semibold' ? 600 : 700;
  }
  
  // Text variant
  if (text) {
    const textStyle = TEXT_VARIANTS[text];
    style.color = textStyle.color;
    style.fontSize = textStyle.fontSize;
    style.fontWeight = parseInt(textStyle.fontWeight);
  }
  
  // Get color from CSS variable
  const getColor = (varName: string, fallback: string): string => {
    if (typeof window === 'undefined') return fallback;
    const computed = getComputedStyle(document.documentElement).getPropertyValue(varName);
    return computed.trim() || fallback;
  };
  
  // Colors from theme
  const colors = {
    primary: getColor('--color-primary', '#0B3D6B'),
    secondary: getColor('--color-secondary', '#0ea5e9'),
    text: getColor('--color-text', '#0f172a'),
    textSecondary: getColor('--color-text-secondary', '#475569'),
    textMuted: getColor('--color-text-muted', '#64748b'),
    background: getColor('--color-background', '#ffffff'),
    surface: getColor('--color-surface', '#f8fafc'),
    border: getColor('--color-border', '#e2e8f0'),
  };
  
  // Card variables
  const card = {
    background: getColor('--glass-card-bg', 'rgba(255,255,255,0.92)'),
    border: getColor('--glass-card-border', '1px solid rgba(11,61,107,0.15)'),
    borderRadius: getColor('--glass-card-border-radius', '16px'),
    padding: getColor('--glass-card-padding', '16px'),
    shadow: getColor('--glass-card-shadow', '0 4px 6px -1px rgba(0,0,0,0.1)'),
    shadowHover: getColor('--glass-card-shadow-hover', '0 10px 15px -3px rgba(0,0,0,0.15)'),
    titleColor: getColor('--glass-card-title-color', '#0f172a'),
    subtitleColor: getColor('--glass-card-subtitle-color', '#475569'),
    textColor: getColor('--glass-card-text-color', '#64748b'),
  };
  
  // Sidebar variables
  const sidebar = {
    background: getColor('--glass-sidebar-bg', '#ffffff'),
    border: getColor('--glass-sidebar-border', '#e2e8f0'),
    width: getColor('--glass-sidebar-width', '280px'),
    collapsedWidth: getColor('--glass-sidebar-collapsed-width', '80px'),
    textColor: getColor('--glass-sidebar-text-color', '#374151'),
    iconColor: getColor('--glass-sidebar-icon-color', '#6b7280'),
    hoverBg: getColor('--glass-sidebar-hover-bg', '#f3f4f6'),
    activeBg: getColor('--glass-sidebar-active-bg', '#0B3D6B20'),
    activeColor: getColor('--glass-sidebar-active-text-color', '#0B3D6B'),
  };
  
  // Header variables
  const header = {
    background: getColor('--glass-header-bg', '#ffffff'),
    border: getColor('--glass-header-border-bottom', '#e2e8f0'),
    height: getColor('--header-height', '64px'),
    textColor: getColor('--glass-header-text-color', '#374151'),
    iconColor: getColor('--glass-header-icon-color', '#6b7280'),
  };
  
  // Bottom Nav variables
  const bottomNav = {
    background: getColor('--glass-bottomnav-bg', '#ffffff'),
    border: getColor('--glass-bottomnav-border-top', '#e2e8f0'),
    height: getColor('--glass-bottomnav-height', '64px'),
    iconColor: getColor('--glass-bottomnav-icon-color', '#64748b'),
    iconActiveColor: getColor('--glass-bottomnav-icon-active-color', '#0B3D6B'),
    textColor: getColor('--glass-bottomnav-text-color', '#475569'),
  };
  
  // Layout variables
  const layout = {
    maxWidth: getColor('--layout-max-width', '1400px'),
    padding: getColor('--layout-padding', '16px'),
    gridGap: getColor('--layout-grid-gap', '16px'),
    containerBg: getColor('--layout-container-bg', '#ffffff'),
    containerBorder: getColor('--layout-container-border', '#e2e8f0'),
  };
  
  // Typography variables
  const typography = {
    fontSizeXs: getColor('--text-size-xs', '0.75rem'),
    fontSizeSm: getColor('--text-size-sm', '0.875rem'),
    fontSizeBase: getColor('--text-size-base', '1rem'),
    fontSizeLg: getColor('--text-size-lg', '1.125rem'),
    fontSizeXl: getColor('--text-size-xl', '1.25rem'),
    fontSize2xl: getColor('--text-size-2xl', '1.5rem'),
    fontWeightNormal: getColor('--text-weight-normal', '400'),
    fontWeightMedium: getColor('--text-weight-medium', '500'),
    fontWeightSemibold: getColor('--text-weight-semibold', '600'),
    fontWeightBold: getColor('--text-weight-bold', '700'),
  };
  
  // Boat card variables
  const boatCard = {
    imageBorderRadius: getColor('--boat-card-image-border-radius', '12px'),
    imageAspectRatio: getColor('--boat-card-image-aspect-ratio', '16/9'),
    titleFontSize: getColor('--boat-card-title-font-size', '18px'),
    titleFontWeight: getColor('--boat-card-title-font-weight', '600'),
    titleColor: getColor('--boat-card-title-color', '#000000'),
    categoryFontSize: getColor('--boat-card-category-font-size', '10px'),
    categoryColor: getColor('--boat-card-category-color', '#888888'),
    yearColor: getColor('--boat-card-year-color', '#666666'),
    priceFontSize: getColor('--boat-card-price-font-size', '17px'),
    priceFontWeight: getColor('--boat-card-price-font-weight', '600'),
    priceColor: getColor('--boat-card-price-color', '#0B3D6B'),
  };
  
  // Helper functions
  const getCardStyle = (): React.CSSProperties => ({
    background: card.background,
    border: card.border,
    borderRadius: card.borderRadius,
    padding: card.padding,
    boxShadow: card.shadow,
  });
  
  const getTextStyle = (variant?: ThemeTextVariant): React.CSSProperties => {
    if (!variant) return {};
    const textStyle = TEXT_VARIANTS[variant];
    return {
      color: textStyle.color,
      fontSize: textStyle.fontSize,
      fontWeight: parseInt(textStyle.fontWeight),
    };
  };
  
  const getSpacingStyle = (type: 'padding' | 'margin', size?: ThemeSpacing): React.CSSProperties => {
    const value = size ? SPACING_MAP[size] : '16px';
    return { [type]: value };
  };
  
  const getGlassStyle = (): React.CSSProperties => ({
    background: getColor('--glass-bg-primary', 'rgba(255,255,255,0.92)'),
    backdropFilter: 'blur(12px)',
    WebkitBackdropFilter: 'blur(12px)',
  });
  
  return {
    className: classNames.join(' '),
    style,
    getCardStyle,
    getTextStyle,
    getSpacingStyle,
    getGlassStyle,
    colors,
    card,
    sidebar,
    header,
    bottomNav,
    layout,
    typography,
    boatCard,
  };
}

export default useThemeStyled;
